import React, { useState, useEffect, useContext, useRef } from "react"

import * as Yup from "yup"
import { Formik, Form, Field } from "formik"
import { toast } from "react-toastify"

import TextField from "@material-ui/core/TextField"
import DialogContent from "@material-ui/core/DialogContent"
import CircularProgress from "@material-ui/core/CircularProgress"

import api from "../../services/api"
import toastError from "../../errors/toastError"
import { AuthContext } from "../../context/Auth/AuthContext"
import { IconButton, InputAdornment } from "@material-ui/core"
import DefaultButton from "../DefaultButton"

import useStyles from "./TagModalStyles"
import Modal from "../Modal"
import ColorPicker from "../ColorPicker"
import { ChevronDown } from "lucide-react"
import Skeleton from "@material-ui/lab/Skeleton"

// Skeleton simples para o modal
const TagModalSkeleton = () => (
  <DialogContent style={{ minHeight: 180 }}>
    <div style={{ marginBottom: 16 }}>
      <Skeleton animation="wave" variant="rect" width="100%" height={48} style={{ borderRadius: 8 }} />
    </div>
    <div style={{ marginBottom: 16 }}>
      <Skeleton animation="wave" variant="rect" width="100%" height={48} style={{ borderRadius: 8 }} />
    </div>
    <div style={{ marginBottom: 16 }}>
      <Skeleton animation="wave" variant="rect" width="30%" height={48} style={{ borderRadius: 8 }} />
    </div>
  </DialogContent>
)

const TagSchema = Yup.object().shape({
  name: Yup.string().trim().min(3, "Nome muito curto").max(30, "Nome muito longo").required("Obrigatório"),
})

const TagModal = ({ open, onClose, situacaoId, reload }) => {
  const classes = useStyles()
  const { user } = useContext(AuthContext)
  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false)
  const [lColorPickerAberto, setColorPickerAberto] = useState(false)
  const [oElementoAncoraCor, setElementoAncoraCor] = useState(null)

  const formikRef = useRef()

  const initialState = {
    name: "",
    color: "",
    kanban: 0,
    ordem: "",
  }

  const [situacao, setTag] = useState(initialState)
  const [kanban, setKanban] = useState(0)
  const [lCarregando, setCarregando] = useState(false)

  useEffect(() => {
    let ativo = true
    const carregar = async () => {
      if (!situacaoId) {
        setTag(initialState)
        setKanban(0)
        setCarregando(false)
        return
      }
      setCarregando(true)
      try {
        const { data } = await api.get(`/situacoes/${situacaoId}`)
        if (ativo) {
          setKanban(data.kanban)
          setTag((prevState) => ({ ...prevState, ...data }))
        }
      } catch (err) {
        toastError(err)
      }
      setCarregando(false)
    }
    carregar()
    return () => {
      ativo = false
    }
    // eslint-disable-next-line
  }, [situacaoId, open])

  const handleClose = () => {
    handleClean()
    onClose()
  }

  const handleClean = () => {
    setTag(initialState)
    setColorPickerModalOpen(false)
  }

  const handleKanbanChange = (e) => {
    setKanban(e.target.checked ? 1 : 0)
  }

  const handleSaveTag = async (values) => {
    const situacaoData = { ...values, userId: user.id, kanban, ordem: values.ordem === "" ? null : values.ordem }
    try {
      if (situacaoId) {
        await api.put(`/situacoes/${situacaoId}`, situacaoData)
      } else {
        await api.post("/situacoes", situacaoData)
      }
      toast.success("Situação salva com sucesso.")
      if (typeof reload == "function") {
        reload()
      }
      handleClose()
    } catch (err) {
      toastError(err)
    }
  }

  const botoes = (
    <div className={classes.buttonsContainer}>
      <DefaultButton
        stylization="primary"
        type="button"
        onClick={() => formikRef.current?.submitForm()}
        disabled={formikRef.current?.isSubmitting}
        className={classes.btnFullWidth}
      >
        Gravar
        {formikRef.current?.isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
      </DefaultButton>
    </div>
  )

  return (
    <div className={classes.root}>
      <Modal
        title={situacaoId ? `Editar situação` : `Nova situação`}
        modalOpen={open}
        onClose={onClose}
        cleanFunc={handleClean}
        footer={botoes}
      >
        {lCarregando ? (
          <TagModalSkeleton />
        ) : (
          <Formik
            innerRef={formikRef}
            initialValues={situacao}
            enableReinitialize={true}
            validationSchema={TagSchema}
            onSubmit={(values, actions) => {
              setTimeout(() => {
                handleSaveTag(values)
                actions.setSubmitting(false)
              }, 400)
            }}
          >
            {({ touched, errors, isSubmitting, values, setFieldValue, handleChange }) => (
              <Form>
                <DialogContent className={classes.content}>
                  <div className={classes.multFieldLine}>
                    <Field
                      as={TextField}
                      placeholder="Nome"
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      onChange={handleChange}
                      className={classes.background + " " + classes.textField}
                      fullWidth
                    />
                  </div>
                  <TextField
                    label="Ordem"
                    variant="outlined"
                    className={classes.background + " " + classes.textField}
                    name="ordem"
                    type="number"
                    value={values.ordem || ""}
                    inputProps={{ min: 5, max: 999 }}
                    onChange={handleChange}
                    fullWidth
                  />
                  <br />
                  <div className={classes.colorboxContainer}>
                    <Field
                      as={TextField}
                      name="color"
                      id="color"
                      error={touched.color && Boolean(errors.color)}
                      helperText={touched.color && errors.color}
                      value={""}
                      InputProps={{
                        disableUnderline: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <div
                              style={{
                                backgroundColor: values.color,
                                borderRadius: "100%",
                                height: "24px",
                                width: "24px",
                              }}
                              className={classes.colorAdorment}
                            ></div>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            size="small"
                            color="default"
                            onClick={(oEvento) => {
                              setElementoAncoraCor(oEvento.currentTarget)
                              setColorPickerAberto(true)
                            }}
                          >
                            <ChevronDown />
                          </IconButton>
                        ),
                      }}
                      className={`${classes.colorbox} ${
                        touched.color && errors.color ? classes.errorField : classes.background
                      } ${classes.background}`}
                      style={{ width: "30%" }}
                    />
                  </div>

                  <ColorPicker
                    lAberto={lColorPickerAberto}
                    aoFechar={() => setColorPickerAberto(false)}
                    aoMudar={(cCor) => {
                      setFieldValue("color", cCor)
                    }}
                    cCorAtual={values.color}
                    oElementoAncora={oElementoAncoraCor}
                  />
                </DialogContent>
              </Form>
            )}
          </Formik>
        )}
      </Modal>
    </div>
  )
}

export default TagModal
